import React, { useEffect, useState } from "react";
import "./tour.css";

/** Highlights `step.selector` on the page with a red ring + an anchored tooltip card. */
export default function TourOverlay({ step, stepNumber, totalSteps, isLast, onNext, onPrev, onSkip }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    setRect(null);
    if (!step) return undefined;

    let target = null;
    let settleTimer = null;
    let cancelled = false;

    const update = () => {
      if (target) setRect(target.getBoundingClientRect());
    };

    // The target may not exist yet (e.g. the page is still loading its data),
    // so poll briefly instead of querying only once on mount. Gives up after
    // ~15s so a wrong/stale selector can't spin a timer forever.
    let attempts = 0;
    const pollTimer = setInterval(() => {
      if (cancelled) return;
      attempts += 1;
      const found = document.querySelector(step.selector);
      if (!found) {
        if (attempts > 100) clearInterval(pollTimer);
        return;
      }
      clearInterval(pollTimer);
      target = found;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      update();
      settleTimer = setTimeout(update, 300); // after the smooth scroll settles
    }, 150);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      clearTimeout(settleTimer);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [step]);

  if (!step || !rect) return null;

  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  const cardWidth = Math.min(320, viewportW - 24);
  const spaceBelow = viewportH - rect.bottom;
  const placeAbove = spaceBelow < 220 && rect.top > 220;

  const cardStyle = {
    left: Math.max(12, Math.min(rect.left, viewportW - cardWidth - 12)),
    top: placeAbove ? Math.max(12, rect.top - 12) : Math.min(rect.bottom + 12, viewportH - 12),
    transform: placeAbove ? "translateY(-100%)" : "none",
    width: cardWidth,
  };

  return (
    <div className="sq-tour-root">
      <div
        className="sq-tour-ring"
        style={{
          left: rect.left - 6,
          top: rect.top - 6,
          width: rect.width + 12,
          height: rect.height + 12,
        }}
      />
      <div className="sq-tour-card" style={cardStyle}>
        <div className="sq-tour-step-count">
          Step {stepNumber} of {totalSteps}
        </div>
        <div className="sq-tour-title">{step.title}</div>
        <div className="sq-tour-text">{step.text}</div>
        <div className="sq-tour-actions">
          <button type="button" className="sq-tour-skip" onClick={onSkip}>
            Skip tour
          </button>
          <div className="sq-tour-actions-right">
            {stepNumber > 1 && (
              <button type="button" className="sq-tour-btn sq-tour-btn-ghost" onClick={onPrev}>
                Back
              </button>
            )}
            <button type="button" className="sq-tour-btn sq-tour-btn-primary" onClick={onNext}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
